<!-- --------------------------------------------------------------------------- -->
<!-- Application Toolbar                                                         -->
<!-- --------------------------------------------------------------------------- -->

<template>
  <v-app-bar v-if="!isHomeScreen" app flat :color="appBarColor" elevation="0" class="app-bar" :style="appBarStyle">
    <router-view name="header" class="pa-0 ma-0 my-0 py-0"></router-view>
    <v-spacer v-if="!xs"></v-spacer>

    <!-- Non-production hint -->
    <div v-if="!isProduction && !xs" style="position: fixed; font-size: 10px; top: 0; right: 10px; color: white">
      breakpoint name:
      {{ breakpoint.name }}
    </div>

    <!-- The search icon opening a dialog for searching -->
    <search v-if="!features.searchDisabled && !xs" :color="color" />

    <v-app-bar-nav-icon
      v-if="!xs"
      :class="`app-bar-nav-icon ml-0 pl-0 ${color ? color + '--text' : ''}`"
      style="pointer-events: auto"
      :small="xs"
      @click.stop="$store.commit('toggleNavigationDrawer')"
    />
  </v-app-bar>

  <v-app-bar v-else app flat :color="appBarColor" elevation="1" class="app-bar" :style="appBarStyle">
    <!--Applicable logo -->
    <div v-if="isLogoVisible" class="pt-1" style="overflow: hidden">
      <logo-large v-if="isHomeScreen" :color="color" />
      <logo-generated v-else-if="!isProduction" />
      <logo-img v-else-if="isLogoVisible"></logo-img>
    </div>

    <!-- Navigation back to home screen -->
    <v-btn v-if="isNavigateBackVisible" @click="navigateBack()" icon>
      <v-icon :color="color">mdi-home</v-icon>
    </v-btn>

    <!-- Home screen title (tenant short or fullName)-->
    <v-toolbar-title v-if="hasHomeTitle" :class="color + '--text'" :style="theme.appBar.home.titleStyle[deviceRange]">
      {{ breadcrumb }}
    </v-toolbar-title>

    <!-- Breadcrumb on all other screens -->
    <v-badge
      v-else-if="!isHomeScreen && moduleUnreadCount > 0"
      :dark="darkMode"
      :color="badgeColor"
      :content="moduleUnreadCount"
      overlap
      offset-x="4"
      offset-y="12"
    >
      <v-toolbar-title :class="color + '--text'" :style="`padding-left:3px !important`"> {{ breadcrumb }} </v-toolbar-title>
    </v-badge>
    <v-toolbar-title v-else-if="!isHomeScreen" :class="color + '--text'" :style="`padding-left:3px !important`">
      {{ breadcrumb }}
    </v-toolbar-title>
    <!-- <v-toolbar-title class="caption align-baseline ml-14 font-weight-light font-italic">{{ stageText }} </v-toolbar-title> -->
    <div v-if="!isProduction && !xs" style="position: fixed; font-size: 12px; top: 0; right: 10px; color: red">
      breakpoint name:
      {{ breakpoint.name }}
    </div>
    <v-spacer></v-spacer>
    <router-view v-if="$route.meta.hideToolbar === true" name="topHeader" class="pa-0"></router-view>
    <!-- The search icon opening a dialog for searching -->
    <search v-if="!features.searchDisabled" :color="color" />
    <span :class="`hidden-sm-and-down ml-8 mr-0 ${color ? color + '--text' : ''}`">Menu</span>

    <v-app-bar-nav-icon :class="`app-bar-nav-icon ml-0 pl-0 ${color ? color + '--text' : ''}`" @click.stop="$store.commit('toggleNavigationDrawer')" />
    <!-- </div> -->
    <template v-slot:extension v-if="!isHomeScreen && !$route.meta.hideToolbar">
      <router-view name="header" style="z-index: -1; background: inherit" class="pa-0" />
    </template>
  </v-app-bar>
</template>
<script>
import LogoGenerated from '@/components/logo/LogoGenerated';
import LogoImg from '@/components/logo/LogoImg';
import LogoLarge from '@/components/logo/LogoLarge';
import { mapState } from 'vuex';
import Search from '@/components/search/Search.vue';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import navigationMixin from '@/mixins/navigationMixin';

export default {
  name: 'AppBar',
  mixins: [common, configMixin, navigationMixin],
  components: { LogoGenerated, LogoImg, LogoLarge, Search },

  data: () => ({
    currentModule: {}, // current module config
    isSearchResultsShown: false,
  }),

  methods: {
    isSafari() {
      let ua = navigator.userAgent.toLowerCase();
      let isSafari = false;
      if (ua.indexOf('safari') != -1) {
        isSafari = ua.indexOf('chrome') < 0;
      }
      //console.debug('navigator.userAgent=', isSafari, navigator.userAgent);
      return isSafari;
    },

    navigateBack() {
      this.$router.push('/');
    },
  },
  computed: {
    ...mapState({
      currentSync: (state) => state.sync.currentSync,
    }),
    appBarColor() {
      if (!(this.theme.appBar.home && this.theme.appBar.other)) {
        console.debug('Configuration issue. Please check ui-config.js theme.appBar in light and dark theme');
        return 'orange';
      }
      if (this.isHomeScreen) {
        let bg = this.theme.appBar.home.bg;
        return this.darkMode ? this.orDefault(bg, 'background darken-1') : this.orDefault(bg, 'background');
      }
      let bg = this.theme.appBar.other.bg;
      return this.darkMode ? this.orDefault(bg, 'background darken-1') : this.orDefault(bg, 'background');
    },
    stageText() {
      let stage = this.$store.state.configs.stage;
      if (stage === 'production' || stage === 'prod') return '';
      return '"' + stage + '" Umgebung';
    },
    /**
     * Color used for texts and icons
     */
    color() {
      return this.isHomeScreen ? this.theme.appBar.home.color : this.theme.appBar.other.color;
    },
    appBarStyle() {
      let transform;
      if (this.isToolbarsHidden) {
        transform = this.xs ? 'translateY(-56px)' : 'translateY(-65px)';
      }
      return { maxWidth: '1904px', transform, marginLeft: 'auto', marginRight: 'auto', touchAction: 'none' };
    },

    moduleUnreadCount() {
      let moduleConfig = this.getModuleConfig(this.$route.meta.moduleType, this.$route.params.instance);
      return this.mx_unreadCount(moduleConfig);
    },
    moduleTitle() {
      if (this.$route.meta.type === 'View') {
        return this.$route.meta.viewConfig.title;
      }
      let moduleConfig = this.$store.getters['configs/getInstanceModuleConfig']({
        moduleType: this.$route.meta.moduleType,
        instance: this.$route.params.instance,
      });

      return this.$t(moduleConfig.title);
    },

    // Search field states
    isSearchExpanded() {
      if (!this.smAndDown) return true;

      return this.searchIsExpanded;
    },

    isNavigateBackVisible() {
      return !this.isHomeScreen;
    },
    isLogoVisible() {
      if (!this.isHomeScreen) return false;
      if (!this.smAndDown) return true;
      return !this.isSearchExpanded;
    },
    hasHomeTitle() {
      return this.isHomeScreen && this.theme.appBar.home.title;
    },
    breadcrumb() {
      const path = this.$route.path;
      if (path.length < 2) {
        return this.$store.state.profile.customerRelations[this.smAndDown ? 'tenantShortName' : 'tenantFullName'];
      }
      const meta = this.$route.meta;
      if (meta.moduleType === 'View') {
        return this.$t(this.$route.name);
      } else if (meta.baseRoute) {
        return this.moduleTitle;
      } else if (meta.baseRoute === false) {
        return this.moduleTitle;
      } else {
        return this.$store.state.profile.customerRelations[this.xs ? 'tenantShortName' : 'tenantFullName'];
      }
    },
    badgeColor() {
      return this.isToAcknowledge ? this.theme.toAcknowledgeBadgeColor : this.theme.unreadBadgeColor;
    },
  },

  watch: {
    $route(to, from) {
      this.currentModule = to.meta.moduleConfig;
    },
    search(to, from) {
      this.performSearch();
    },
  },
};
</script>

<style scoped>
.app-bar {
  transition: transform 500ms ease-in-out;
  margin-left: auto;
  margin-right: auto;
}

.v-toolbar__extension {
  padding: 0px !important;
}
</style>
